import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import AppDateInput from '../../../components/AppDateInput'
import AppCombobox from '../../../components/AppCombobox'
import AppDropdown from '../../../components/AppDropdown'
import AppToggle from '../../../components/AppToggle'
import { useProjectEntryScreen } from './hooks/useProjectEntryScreen'
import { appToast } from '../../../components/AppToast'
import { useAuth } from '../../../hooks/useAuth'

// Gap 6: Click-to-Call — useTelephony is optional; gracefully skip if not configured
let useTelephonyHook = null
try {
  // eslint-disable-next-line
  useTelephonyHook = require('../../../telephony/context/TelephonyContext').useTelephony
} catch {}

const FALLBACK_ROLES = ['OWNER', 'POC', 'POC_MANAGER', 'ACCOUNTANT']

function initialsFromName(name) {
  return String(name || 'SM')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

function emptyStakeholderDraft(defaultRole = '') {
  return { role: defaultRole, name: '', phoneNumbers: [''], email: '' }
}

function isValidStakeholderPhone(value) {
  const text = String(value || '').trim()
  const digits = text.replace(/\D/g, '')
  return /^[0-9+\-()\s]+$/.test(text) && digits.length === 10
}

function isValidStakeholderEmail(value) {
  const text = String(value || '').trim()
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)
}

function inclusiveDaysBetween(startDate, endDate) {
  if (!startDate) return 0
  const start = new Date(startDate)
  const end = new Date(endDate)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0
  const startMid = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  const endMid = new Date(end.getFullYear(), end.getMonth(), end.getDate())
  const diffMs = endMid.getTime() - startMid.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  return Math.max(1, diffDays + 1)
}

function attachmentDisplayName(item) {
  if (!item) return ''
  const fromMeta = String(item.originalName || item.name || '').trim()
  if (fromMeta) return fromMeta
  const path = String(item.path || '').trim()
  return path.split('/').pop()?.split('\\').pop() || path
}

const todayIso = new Date().toISOString().slice(0, 10)

export default function ProjectConfigurationEntryScreen() {
  const { state, actions } = useProjectEntryScreen()
  const [searchParams, setSearchParams] = useSearchParams()

  // Gap 6: Click-to-Call — gracefully handle missing TelephonyContext
  let makeCall = null
  try {
    if (useTelephonyHook) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const tel = useTelephonyHook()
      makeCall = tel?.makeCall || null
    }
  } catch { /* Telephony not configured for this tenant */ }

  // URL-based mode: ?mode=subproject makes Parent Project required and reorders fields
  const isSubProjectMode = searchParams.get('mode') === 'subproject'

  function setMode(mode) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (mode === 'subproject') {
        next.set('mode', 'subproject')
      } else {
        next.delete('mode')
        // Clear parent when switching back to project mode
        actions.patchForm('parent_project_id', '')
      }
      return next
    })
  }

  const { hasPrivilege, isAdmin } = useAuth()
  const canViewFinancials = isAdmin || hasPrivilege('ProjectFinancialsView')
  const canManageFinancials = isAdmin || hasPrivilege('ProjectFinancialsCreate')



  useEffect(() => {
    if (state.error) {
      appToast.error(state.error)
    }
  }, [state.error])

  useEffect(() => {
    if (state.success) {
      appToast.success(state.success)
    }
  }, [state.success])

  const [isStakeholderModalOpen, setIsStakeholderModalOpen] = useState(false)
  const [isDisableTeamModalOpen, setIsDisableTeamModalOpen] = useState(false)
  const [editingStakeholderIndex, setEditingStakeholderIndex] = useState(null)
  const [stakeholderDraftError, setStakeholderDraftError] = useState('')
  const [uploadInputKey, setUploadInputKey] = useState(0)
  const stakeholderRoles = state.stakeholderRoles.length ? state.stakeholderRoles : FALLBACK_ROLES
  const [stakeholderDraft, setStakeholderDraft] = useState(emptyStakeholderDraft(stakeholderRoles[0] || 'OWNER'))
  const teamMemberOptions = useMemo(
    () =>
      state.managerOptions
        .map((option) => ({
          value: String(option.id),
          label: option.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [state.managerOptions],
  )
  const selectedTeamMembers = useMemo(() => {
    const selectedIds = Array.isArray(state.form.team_member_user_ids) ? state.form.team_member_user_ids : []
    const byId = new Map(teamMemberOptions.map((option) => [String(option.value), option.label]))
    return selectedIds.map((id) => {
      const normalizedId = String(id)
      return {
        id: normalizedId,
        name: byId.get(normalizedId) || 'User',
      }
    })
  }, [state.form.team_member_user_ids, teamMemberOptions])

  const visibleContacts = useMemo(
    () =>
      state.stakeholders
        .map((row, sourceIndex) => ({ ...row, sourceIndex }))
        .filter((row) => String(row.name || '').trim())
        .slice(0, 4),
    [state.stakeholders],
  )

  const hasMapping = Boolean(state.form.company_id && (state.form.branch_id || state.form.department_id))
  const estimatedHours = useMemo(() => {
    if (!state.form.start_date) return 0
    const endForCalc = state.form.end_date || todayIso
    const days = inclusiveDaysBetween(state.form.start_date, endForCalc)
    return days * 8
  }, [state.form.end_date, state.form.start_date, todayIso])
  const advanceAmount = Number(state.form.advance_amount || 0)
  const baseReceivedAmount = Number(state.form.base_received_amount || 0)
  const receivedNow = Number(state.form.received_now || 0)
  const isTimeAndMaterialSubProject =
    (isSubProjectMode || state.form.parent_project_id) && String(state.form.billing_type || '').toUpperCase() === 'TIME_AND_MATERIAL'
  const activeReceivedNow = isTimeAndMaterialSubProject ? 0 : receivedNow
  const receivedAmount = Math.max(0, baseReceivedAmount + activeReceivedNow)
  const totalReceivedAmount = Math.max(0, advanceAmount + receivedAmount)
  const parentBillingMode = (isSubProjectMode || state.form.parent_project_id)
    ? (state.form.parent_project_billing_mode || 'AMC')
    : (state.form.project_billing_mode || 'AMC')
  const hasPartialStakeholder = useMemo(
    () =>
      state.stakeholders.some((row) => {
        const role = String(row.role || '').trim()
        const name = String(row.name || '').trim()
        const phone = String(row.phone || '').trim()
        const email = String(row.email || '').trim()
        const hasAny = Boolean(role || name || phone || email)
        const isFull = Boolean(role && name && phone && email)
        return hasAny && !isFull
      }),
    [state.stakeholders],
  )
  const stakeholderPhoneError = useMemo(() => {
    const phoneNumbers = Array.isArray(stakeholderDraft.phoneNumbers) ? stakeholderDraft.phoneNumbers : []
    const hasInvalid = phoneNumbers.some(p => p.trim() && !isValidStakeholderPhone(p))
    return hasInvalid ? 'One or more phone numbers are invalid.' : ''
  }, [stakeholderDraft.phoneNumbers])
  const stakeholderEmailError = useMemo(() => {
    const email = String(stakeholderDraft.email || '').trim()
    if (!email) return ''
    return isValidStakeholderEmail(email) ? '' : 'Enter a valid email address.'
  }, [stakeholderDraft.email])

  function onOpenStakeholderModal() {
    setEditingStakeholderIndex(null)
    setStakeholderDraftError('')
    setStakeholderDraft(emptyStakeholderDraft(stakeholderRoles[0] || 'OWNER'))
    setIsStakeholderModalOpen(true)
  }

  function onEditStakeholder(index) {
    const row = state.stakeholders[index]
    if (!row) return
    setEditingStakeholderIndex(index)
    setStakeholderDraftError('')
    setStakeholderDraft({
      role: row.role || stakeholderRoles[0] || 'OWNER',
      name: row.name || '',
      phoneNumbers: Array.isArray(row.phoneNumbers)
        ? [...row.phoneNumbers]
        : Array.isArray(row.phone_numbers)
        ? [...row.phone_numbers]
        : row.phone
        ? [row.phone]
        : [''],
      email: row.email || '',
    })
    setIsStakeholderModalOpen(true)
  }

  function onCloseStakeholderModal() {
    setIsStakeholderModalOpen(false)
    setEditingStakeholderIndex(null)
    setStakeholderDraftError('')
  }

  function onSubmitStakeholder() {
    const role = String(stakeholderDraft.role || '').trim()
    const name = String(stakeholderDraft.name || '').trim()
    const email = String(stakeholderDraft.email || '').trim()
    const phoneNumbers = Array.isArray(stakeholderDraft.phoneNumbers)
      ? stakeholderDraft.phoneNumbers.map(p => String(p || '').trim()).filter(Boolean)
      : []
    if (!role || !name || phoneNumbers.length === 0 || !email) {
      setStakeholderDraftError('Role, name, phone number(s), and email are required.')
      return
    }
    const hasInvalidPhone = phoneNumbers.some(p => !isValidStakeholderPhone(p))
    if (hasInvalidPhone) {
      setStakeholderDraftError('One or more phone numbers contain invalid characters or are too short.')
      return
    }
    if (!isValidStakeholderEmail(email)) {
      setStakeholderDraftError('Invalid email format.')
      return
    }
    const payload = { role, name, phoneNumbers, email }
    if (editingStakeholderIndex === null) {
      actions.addStakeholderRow(payload)
    } else {
      actions.patchStakeholder(editingStakeholderIndex, 'role', payload.role)
      actions.patchStakeholder(editingStakeholderIndex, 'name', payload.name)
      actions.patchStakeholder(editingStakeholderIndex, 'phoneNumbers', payload.phoneNumbers)
      actions.patchStakeholder(editingStakeholderIndex, 'email', payload.email)
    }
    setIsStakeholderModalOpen(false)
    setEditingStakeholderIndex(null)
    setStakeholderDraftError('')
  }

  function onTeamToggleChange(next) {
    const nextValue = Boolean(next)
    if (!nextValue && Boolean(state.form.auto_create_team)) {
      if (!state.canDisableTeamToggle) {
        actions.setErrorMessage('ProjectTeamDisable privilege is required to disable team setup.')
        return
      }
      setIsDisableTeamModalOpen(true)
      return
    }
    actions.patchForm('auto_create_team', nextValue)
  }

  return (
    <div className="mx-auto w-full max-w-6xl pb-10">
      <header className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <span className="mb-2 block text-xs font-bold uppercase tracking-widest text-primary">Configuration Portal</span>
          <h1 className="text-4xl font-extrabold tracking-tight text-on-surface md:text-5xl">
            {state.isEditMode
              ? 'Project Configuration'
              : isSubProjectMode
              ? 'New Sub-Project Entry'
              : 'New Project Entry'}
          </h1>
          {!state.isEditMode && (
            <p className="mt-2 text-sm text-on-surface-variant">
              {isSubProjectMode
                ? 'Select a parent project and fill in the sub-project scope below.'
                : 'Configure a new standalone project and its organizational mapping.'}
            </p>
          )}
        </div>

        {/* Mode toggle — only shown in create mode */}
        {!state.isEditMode && (
          <div className="flex items-center gap-1 rounded-xl bg-surface-container-low p-1 shadow-inner">
            <button
              type="button"
              id="mode-project-btn"
              onClick={() => setMode('project')}
              className={`rounded-lg px-5 py-2.5 text-sm font-bold transition-all ${
                !isSubProjectMode
                  ? 'bg-primary text-on-primary shadow-sm'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              <span className="material-symbols-outlined align-middle text-base mr-1.5" style={{ verticalAlign: '-3px' }}>folder</span>
              Project Entry
            </button>
            <button
              type="button"
              id="mode-subproject-btn"
              onClick={() => setMode('subproject')}
              className={`rounded-lg px-5 py-2.5 text-sm font-bold transition-all ${
                isSubProjectMode
                  ? 'bg-primary text-on-primary shadow-sm'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              <span className="material-symbols-outlined align-middle text-base mr-1.5" style={{ verticalAlign: '-3px' }}>account_tree</span>
              Sub-Project Entry
            </button>
          </div>
        )}
      </header>

      {/* Sub-project mode info banner */}
      {isSubProjectMode && !state.isEditMode && (
        <div className="mb-8 flex items-start gap-3 rounded-xl border border-primary/20 bg-primary-container/15 px-5 py-4">
          <span className="material-symbols-outlined text-primary mt-0.5">info</span>
          <div>
            <p className="text-sm font-bold text-on-surface">Sub-Project Mode Active</p>
            <p className="text-xs text-on-surface-variant mt-0.5">
              Select a Parent Project below. Common fields (Company, Branch, Customer, Manager, Dates) will be
              auto-populated from the parent. Billing Type and Budget are required for sub-projects.
            </p>
          </div>
        </div>
      )}



      {state.isLoading ? (
        <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest px-6 py-16 text-center text-sm text-on-surface-variant">
          Loading project configuration...
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <section className="flex flex-col gap-8 lg:col-span-8">



            <article className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-8">
              <div className="mb-8 flex items-center gap-3">
                <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>
                  info
                </span>
                <h3 className="text-xl font-bold text-on-surface">General Info</h3>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <label className="col-span-full">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                    Project Name <span className="text-error">*</span>
                  </span>
                  <input
                    type="text"
                    value={state.form.name}
                    onChange={(event) => actions.patchForm('name', event.target.value)}
                    placeholder="e.g. Meridian Phase II Development"
                    className="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none transition-all focus:bg-surface-container-lowest focus:ring-2 focus:ring-primary/40"
                  />
                </label>

                <label>
                  <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Project Code</span>
                  <div className="relative">
                    <input
                      type="text"
                      value={state.projectCode || 'PRJ-2024-001'}
                      readOnly
                      className="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface-variant outline-none"
                    />
                    <span className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-error">
                      priority_high
                    </span>
                  </div>
                </label>

                <label>
                  <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Priority</span>
                  <AppDropdown
                    value={state.form.priority}
                    onChange={(value) => actions.patchForm('priority', value)}
                    triggerClassName="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface transition-all"
                    options={[
                      { value: 'LOW', label: 'LOW' },
                      { value: 'MEDIUM', label: 'MEDIUM' },
                      { value: 'HIGH', label: 'HIGH' },
                    ]}
                  />
                </label>

                <label className="col-span-full">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Description</span>
                  <textarea
                    rows={4}
                    value={state.form.description}
                    onChange={(event) => actions.patchForm('description', event.target.value)}
                    placeholder="Detail the primary objectives and structural scope..."
                    className="w-full resize-none rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none transition-all focus:bg-surface-container-lowest focus:ring-2 focus:ring-primary/40"
                  />
                </label>
              </div>
            </article>

            <article className="rounded-2xl bg-surface-container-low p-8">
              <div className="mb-8 flex items-center gap-3">
                <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>
                  account_tree
                </span>
                <h3 className="text-xl font-bold text-on-surface">Organization Mapping</h3>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                {/* In sub-project mode, Parent Project is shown at the top and is required */}
                {(isSubProjectMode || state.form.parent_project_id) && (
                  <label className="md:col-span-3">
                    <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                      Parent Project {isSubProjectMode && <span className="text-error">*</span>}
                    </span>
                    <AppCombobox
                      value={state.form.parent_project_id}
                      onChange={actions.handleParentProjectChange}
                      placeholder={isSubProjectMode ? 'Select a parent project...' : 'None (This is a parent project)'}
                      triggerClassName="w-full rounded-md border-none bg-surface-container-lowest px-4 py-3 text-base text-on-surface transition-all"
                      options={state.parentProjectOptions.map((option) => ({
                        value: String(option.id),
                        label: `${option.name} (${option.project_code})`,
                      }))}
                      allowClear={!isSubProjectMode}
                    />
                    {state.form.parent_project_id && (
                      <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-primary">
                        <span className="material-symbols-outlined text-xs">check_circle</span>
                        Common fields auto-populated from parent project.
                      </p>
                    )}
                    {isSubProjectMode && !state.form.parent_project_id && (
                      <p className="mt-1 text-[11px] text-on-surface-variant">
                        Required. Selecting a parent will auto-fill Company, Branch, Customer, Manager, and Dates.
                      </p>
                    )}
                  </label>
                )}

                <label>
                  <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                    Company <span className="text-error">*</span>
                  </span>
                  <AppDropdown
                    value={state.form.company_id}
                    onChange={actions.handleCompanyChange}
                    disabled={isSubProjectMode && Boolean(state.form.parent_project_id)}
                    placeholder="Select Company"
                    triggerClassName="w-full rounded-md border-none bg-surface-container-lowest px-4 py-3 text-base text-on-surface transition-all disabled:opacity-70"
                    fetchOptions={actions.fetchCompanyOptions}
                    initialLabel={state.initialLabels.company_name}
                  />
                </label>

                <label>
                  <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Branch</span>
                  <AppDropdown
                    value={state.form.branch_id}
                    onChange={actions.handleBranchChange}
                    disabled={!state.form.company_id}
                    placeholder={state.form.company_id ? 'Select Branch' : 'Select company first'}
                    triggerClassName="w-full rounded-md border-none bg-surface-container-lowest px-4 py-3 text-base text-on-surface transition-all disabled:cursor-not-allowed disabled:opacity-60"
                    fetchOptions={actions.fetchBranchOptions}
                    initialLabel={state.initialLabels.branch_name}
                  />
                </label>

                <label>
                  <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Department</span>
                  <AppDropdown
                    value={state.form.department_id}
                    onChange={(value) => actions.patchForm('department_id', value)}
                    disabled={!state.form.company_id}
                    placeholder={state.form.company_id ? 'Select Department' : 'Select company first'}
                    triggerClassName="w-full rounded-md border-none bg-surface-container-lowest px-4 py-3 text-base text-on-surface transition-all disabled:cursor-not-allowed disabled:opacity-60"
                    fetchOptions={actions.fetchDepartmentOptions}
                    initialLabel={state.initialLabels.department_name}
                  />
                </label>

              </div>
            </article>

            <article className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-8">
              <div className="mb-8 flex items-center gap-3">
                <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>
                  payments
                </span>
                <h3 className="text-xl font-bold text-on-surface">Financials</h3>
              </div>

              <div className="space-y-6">
                <div className="flex items-center justify-between rounded-lg bg-surface-container-low p-4">
                  <div>
                    <p className="text-xl font-bold text-on-surface">Billable Status</p>
                    <p className="text-sm text-on-surface-variant">
                      {canManageFinancials
                        ? 'Enable billing fields only when project is billable.'
                        : 'Financial configuration requires ProjectFinancialsCreate privilege.'}
                    </p>
                  </div>
                  <AppToggle
                    disabled={!canManageFinancials}
                    checked={Boolean(state.form.billable)}
                    onChange={(next) => {
                      if (!canManageFinancials) return
                      actions.patchForm('billable', next)
                      if (next && !isSubProjectMode && !state.form.project_billing_mode) {
                        actions.patchForm('project_billing_mode', 'AMC')
                      }
                    }}
                    ariaLabel="Toggle billable status"
                  />
                </div>

                {!canViewFinancials && Boolean(state.form.billable) && (
                  <div className="flex items-center gap-2.5 rounded-lg border border-outline-variant/15 bg-surface-container-low px-4 py-3 text-xs font-semibold text-on-surface-variant/80">
                    <span className="material-symbols-outlined text-sm text-primary">lock</span>
                    <span>Commercial billing parameters are active and restricted to authorized financial roles.</span>
                  </div>
                )}

                <div
                  className={`grid overflow-hidden transition-all duration-300 ${
                    state.form.billable && canViewFinancials
                      ? 'max-h-[1200px] grid-cols-1 gap-5 opacity-100 md:grid-cols-2'
                      : 'max-h-0 grid-cols-1 gap-0 opacity-0 pointer-events-none'
                  }`}
                >
                  {isSubProjectMode ? (
                    <>
                      {/* Sub-Project Billing Settings */}
                      <label>
                        <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Billing Type <span className="text-error">*</span></span>
                        <AppDropdown
                          value={state.form.billing_type}
                          onChange={(value) => actions.patchForm('billing_type', value)}
                          placeholder="Select Billing Type"
                          triggerClassName="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface transition-all"
                          options={[
                            { value: 'FIXED_PRICE', label: 'FIXED PRICE' },
                            { value: 'TIME_AND_MATERIAL', label: 'TIME AND MATERIAL' },
                          ]}
                        />
                      </label>

                      {state.form.billing_type === 'TIME_AND_MATERIAL' ? (
                        <label>
                          <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Hourly Billing Rate (per hour) <span className="text-error">*</span></span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={state.form.billing_rate}
                            onChange={(event) => actions.patchForm('billing_rate', event.target.value)}
                            className="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none"
                            placeholder="e.g. 50.00"
                          />
                        </label>
                      ) : (
                        <label>
                          <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Fixed Budget / Billable Amount <span className="text-error">*</span></span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={state.form.budget}
                            onChange={(event) => actions.patchForm('budget', event.target.value)}
                            className="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none"
                          />
                        </label>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="col-span-full grid grid-cols-2 gap-2 rounded-lg bg-surface-container-low p-1">
                        {[
                          { value: 'AMC', label: 'AMC Revenue Settings' },
                          { value: 'PROJECT_BILLING', label: 'Project Billing Settings' },
                        ].map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => actions.patchForm('project_billing_mode', option.value)}
                            className={`rounded-md px-4 py-3 text-sm font-bold transition-all ${
                              parentBillingMode === option.value
                                ? 'bg-primary text-on-primary shadow-sm'
                                : 'text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                      {parentBillingMode === 'AMC' ? (
                        <>
                      {/* Parent Project AMC Configuration */}
                      {/* <label className="col-span-full border-b border-outline-variant/35 pb-2">
                        <span className="text-sm font-bold text-primary font-black uppercase tracking-wider">AMC Revenue Settings</span>
                      </label> */}

                      <label>
                        <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">AMC Amount</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={state.form.project_amc_amount}
                          onChange={(event) => actions.patchForm('project_amc_amount', event.target.value)}
                          className="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none"
                          placeholder="e.g., 120000"
                        />
                      </label>

                      <label>
                        <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">AMC Currency</span>
                        <AppDropdown
                          value={state.form.project_amc_currency}
                          onChange={(value) => actions.patchForm('project_amc_currency', value)}
                          placeholder="Select Currency"
                          triggerClassName="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface transition-all"
                          options={[
                            { value: 'INR', label: 'INR (₹)' },
                            { value: 'USD', label: 'USD ($)' },
                            { value: 'EUR', label: 'EUR (€)' },
                            { value: 'GBP', label: 'GBP (£)' },
                          ]}
                        />
                      </label>

                      <label>
                        <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">AMC Frequency</span>
                        <AppDropdown
                          value={state.form.project_amc_frequency}
                          onChange={(value) => actions.patchForm('project_amc_frequency', value)}
                          placeholder="Select Frequency"
                          triggerClassName="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface transition-all"
                          options={[
                            { value: 'MONTHLY', label: 'MONTHLY' },
                            { value: 'QUARTERLY', label: 'QUARTERLY' },
                            { value: 'HALF_YEARLY', label: 'HALF YEARLY' },
                            { value: 'YEARLY', label: 'YEARLY' },
                          ]}
                        />
                      </label>

                      <label>
                        <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">AMC Billing Timing</span>
                        <AppDropdown
                          value={state.form.billing_timing || 'START_OF_PERIOD'}
                          onChange={(value) => actions.patchForm('billing_timing', value)}
                          placeholder="Select Timing"
                          triggerClassName="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface transition-all"
                          options={[
                            { value: 'START_OF_PERIOD', label: 'Receive at Start of Period (e.g. 1st of month)' },
                            { value: 'END_OF_PERIOD', label: 'Receive at End of Period (e.g. Last day of month)' },
                          ]}
                        />
                      </label>


                      <div className="grid grid-cols-2 gap-2">
                        <label>
                          <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">AMC Start Date</span>
                          <AppDateInput
                            value={state.form.project_amc_start_date}
                            onChange={(value) => actions.patchForm('project_amc_start_date', value)}
                            inputClassName="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface shadow-none"
                          />
                        </label>

                        <label>
                          <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">AMC End Date</span>
                          <AppDateInput
                            value={state.form.project_amc_end_date}
                            onChange={(value) => actions.patchForm('project_amc_end_date', value)}
                            inputClassName="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface shadow-none"
                          />
                        </label>
                      </div>
                        </>
                      ) : null}

                      {parentBillingMode === 'PROJECT_BILLING' ? (
                        <>
                      {/* <label className="col-span-full border-b border-outline-variant/35 pb-2 mt-4">
                        <span className="text-sm font-bold text-primary font-black uppercase tracking-wider">Project Billing Settings</span>
                      </label> */}

                      <label>
                        <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Fixed Budget / Billable Amount</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={state.form.budget}
                          onChange={(event) => actions.patchForm('budget', event.target.value)}
                          className="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none"
                        />
                      </label>
                        </>
                      ) : null}
                    </>
                  )}

                  {(isSubProjectMode || parentBillingMode === 'PROJECT_BILLING') ? (
                    <label>
                      <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Bill Due Date</span>
                      <AppDateInput
                        value={state.form.bill_due_date}
                        onChange={(value) => actions.patchForm('bill_due_date', value)}
                        inputClassName="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface shadow-none"
                      />
                    </label>
                  ) : null}

                  {(isSubProjectMode || parentBillingMode === 'PROJECT_BILLING') ? (
                    <label>
                      <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Advance Amount</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={state.form.advance_amount}
                        onChange={(event) => actions.patchForm('advance_amount', event.target.value)}
                        className="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none"
                      />
                    </label>
                  ) : null}

                  {!isTimeAndMaterialSubProject && (isSubProjectMode || parentBillingMode === 'PROJECT_BILLING') ? (
                    <label>
                      <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Received Now</span>
                      <input
                        type="number"
                        step="0.01"
                        value={state.form.received_now}
                        onChange={(event) => actions.patchForm('received_now', event.target.value)}
                        className="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none"
                      />
                    </label>
                  ) : null}

                  {(isSubProjectMode || parentBillingMode === 'PROJECT_BILLING') ? (
                    <label className="md:col-span-2">
                      <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Total Received Amount</span>
                      <input
                        type="text"
                        readOnly
                        value={totalReceivedAmount.toFixed(2)}
                        className="w-full rounded-md border-none bg-surface-container-high px-4 py-3 text-sm font-bold text-on-surface outline-none"
                      />
                      <p className="mt-1 text-[11px] text-on-surface-variant">
                        {isTimeAndMaterialSubProject
                          ? 'Time and material revenue is calculated from approved hours and hourly rate.'
                          : 'Computed as Advance Amount + (Existing Received + Received Now).'}
                      </p>
                    </label>
                  ) : null}
                </div>

                {/* {!state.form.billable ? (
                  <p className="rounded-lg bg-surface-container-low px-4 py-3 text-xs text-on-surface-variant">
                    Turn on billable status to configure amount and payment dates.
                  </p>
                ) : null} */}

                <label>
                  <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Notes</span>
                  <textarea
                    rows={4}
                    value={state.form.notes}
                    onChange={(event) => actions.patchForm('notes', event.target.value)}
                    placeholder="Add project notes..."
                    className="w-full resize-none rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none"
                  />
                </label>
              </div>
            </article>

            <article className="rounded-2xl bg-surface-container-low p-8">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold tracking-tight text-on-surface">Technical Assets &amp; Attachments</h2>
                <button
                  type="button"
                  onClick={() => document.getElementById(`project-attachment-input-${uploadInputKey}`)?.click()}
                  disabled={state.isUploadingAttachment}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-bold text-primary hover:bg-primary/10"
                >
                  <span className="material-symbols-outlined text-base">upload_file</span>
                  {state.isUploadingAttachment ? 'Uploading...' : 'Upload New'}
                </button>
                <input
                  id={`project-attachment-input-${uploadInputKey}`}
                  type="file"
                  className="hidden"
                  accept=".png,.jpg,.jpeg,.pdf,.docx"
                  onChange={(event) => {
                    const file = event.target.files?.[0]
                    if (file) actions.handleUploadAttachment(file)
                    setUploadInputKey((prev) => prev + 1)
                  }}
                />
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {state.attachmentItems.length ? (
                  state.attachmentItems.map((item) => {
                    const displayName = attachmentDisplayName(item)
                    const lower = displayName.toLowerCase()
                    const isPdf = lower.endsWith('.pdf')
                    return (
                      <div key={item.id || item.path} className="flex items-center justify-between gap-3 rounded-md bg-surface-container-lowest p-3 ring-1 ring-outline-variant/20">
                        <div className="flex min-w-0 items-center gap-3">
                          <span className={`material-symbols-outlined rounded-full p-2 ${isPdf ? 'bg-error-container text-error' : 'bg-primary-fixed text-primary'}`}>
                            {isPdf ? 'picture_as_pdf' : 'description'}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-lg font-semibold text-on-surface">{displayName}</p>
                            <p className="text-xs uppercase tracking-wider text-on-surface-variant">Attachment</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => actions.removeAttachment(item.id)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-error"
                          aria-label={`Remove ${displayName}`}
                        >
                          <span className="material-symbols-outlined text-base">close</span>
                        </button>
                      </div>
                    )
                  })
                ) : (
                  <div className="rounded-md bg-surface-container-lowest p-4 text-sm text-on-surface-variant">
                    No assets added yet.
                  </div>
                )}
              </div>
            </article>
            {state.isEditMode && !state.form.parent_project_id && state.subProjects && state.subProjects.length > 0 && (
              <article className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-8 shadow-sm">
                <div className="mb-6 flex items-center gap-3">
                  <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>
                    schema
                  </span>
                  <div>
                    <h3 className="text-xl font-bold text-on-surface">Sub-Projects</h3>
                    <p className="text-xs text-on-surface-variant">Linked deliverable scopes under this project</p>
                  </div>
                </div>

                <div className="space-y-2">
                  {state.subProjects.map((sp) => (
                    <a
                      key={sp.id}
                      href={`/nerve/project/entry?id=${sp.id}`}
                      className="flex items-center justify-between rounded-xl border border-outline-variant/10 bg-surface-container-low px-4 py-3 transition hover:bg-surface-container-high group"
                    >
                      <div className="flex items-center gap-3">
                        <span className="material-symbols-outlined text-sm text-primary">account_tree</span>
                        <div>
                          <p className="text-sm font-semibold text-primary group-hover:underline">{sp.name}</p>
                          <p className="text-[10px] text-on-surface-variant font-mono">{sp.projectCode || '—'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        {sp.billable ? (
                          <span className="rounded-full bg-primary-container px-2 py-0.5 text-xs font-bold text-on-primary-container">
                            {String(sp.billingType || '').replaceAll('_', ' ')}
                          </span>
                        ) : (
                          <span className="rounded-full bg-surface-container-high px-2 py-0.5 text-xs font-bold text-on-surface-variant">NON-BILLABLE</span>
                        )}
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                          sp.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                          sp.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>{sp.status || 'DRAFT'}</span>
                        <span className="material-symbols-outlined text-sm text-on-surface-variant group-hover:text-primary">open_in_new</span>
                      </div>
                    </a>
                  ))}
                </div>

                <p className="mt-4 text-[11px] text-on-surface-variant">
                  To create a new sub-project, click "+ Create New" in the header and use Sub-Project Entry mode, selecting this project as the parent.
                </p>
              </article>
            )}
          </section>

          <aside className="flex flex-col gap-8 lg:col-span-4">
            <article className="relative overflow-hidden rounded-2xl bg-primary-container p-8 text-on-primary-container">
              <div className="relative z-10">
                <div className="mb-6 flex items-center gap-3">
                  <span className="text-white material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                    schedule
                  </span>
                  <h3 className="text-white text-xl font-bold">Timeline</h3>
                </div>

                <div className="space-y-6">
                  <div>
                    <p className="text-white mb-2 text-[10px] font-bold uppercase tracking-widest opacity-90">Project Duration</p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <label className="rounded-lg bg-white/10 p-3 backdrop-blur-sm">
                        <p className="text-white text-[10px] font-bold uppercase">
                          Starts <span className="text-error">*</span>
                        </p>
                        <AppDateInput
                          value={state.form.start_date}
                          onChange={(value) => actions.patchForm('start_date', value)}
                          inputClassName="w-full border-none bg-transparent px-0 py-1 pr-8 text-sm font-bold text-white shadow-none tabular-nums"
                          iconClassName="text-white/80"
                          buttonClassName="text-white/80 hover:bg-white/10 hover:text-white"
                        />
                      </label>

                      <label className="rounded-lg bg-white/10 p-3 backdrop-blur-sm">
                        <p className="text-white text-[10px] font-bold uppercase">Ends</p>
                        <AppDateInput
                          value={state.form.end_date}
                          onChange={(value) => actions.patchForm('end_date', value)}
                          inputClassName="w-full border-none bg-transparent px-0 py-1 pr-8 text-sm font-bold text-white shadow-none tabular-nums"
                          iconClassName="text-white/80"
                          buttonClassName="text-white/80 hover:bg-white/10 hover:text-white"
                        />
                      </label>
                    </div>
                  </div>

                  <div>
                    <p className="text-white mb-2 text-[10px] font-bold uppercase tracking-widest opacity-90">Est. Delivery Time</p>
                    <div className="flex items-end gap-2">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={state.form.delivery_hours}
                        onChange={(event) => actions.patchForm('delivery_hours', event.target.value)}
                        placeholder={String(estimatedHours)}
                        className="w-full rounded-lg border-none bg-white/10 px-4 py-3 text-4xl font-black text-white outline-none placeholder:text-white/55"
                      />
                      <span className="text-white mb-2 text-sm font-bold">Engineering Hours</span>
                    </div>
                    <p className="mt-1 text-[10px] font-semibold text-white/70">Auto-suggested: {estimatedHours} hours</p>
                  </div>

                  <div className="border-t border-white/20 pt-4">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-white text-xs font-bold uppercase tracking-widest opacity-80">Setup Completion</span>
                      <span className="text-white text-xs font-bold">{state.completionPercent}%</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-white/20">
                      <div
                        className="h-full rounded-full bg-white shadow-[0_0_15px_rgba(255,255,255,0.5)]"
                        style={{ width: `${Math.max(5, state.completionPercent)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="pointer-events-none absolute -bottom-12 -right-12 h-48 w-48 rounded-full bg-white/10 blur-3xl" />
            </article>

            <article className="rounded-2xl bg-surface-container-high p-8">
              <div className="mb-8 flex items-center gap-3">
                <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>
                  groups
                </span>
                <h3 className="text-xl font-bold text-on-surface">Project Team</h3>
              </div>

              <div className="space-y-6">
                <div className="flex items-center justify-between gap-3 rounded-lg border border-outline-variant/30 bg-surface-container-lowest p-3">
                  <div className="min-w-0 pr-2">
                    <p className="text-sm font-bold text-on-surface">Create Project Channel</p>
                    <p className="text-[11px] text-on-surface-variant">Enable project team + channel sync using manager and selected members.</p>
                  </div>
                  <AppToggle
                    checked={Boolean(state.form.auto_create_team)}
                    onChange={onTeamToggleChange}
                    ariaLabel="Toggle auto team creation"
                  />
                </div>

                <label className="mt-2 block">
                  <span className="mb-3 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Internal RP (Lead)</span>
                  <div className="rounded-lg border border-outline-variant/30 bg-surface-container-lowest p-3">
                    <AppDropdown
                      value={state.form.manager_id}
                      onChange={(value) => actions.patchForm('manager_id', value)}
                      placeholder="Select lead"
                      triggerClassName="w-full border-none bg-transparent p-0 text-sm font-medium text-on-surface"
                      fetchOptions={actions.fetchManagerOptions}
                      initialLabel={state.initialLabels.manager_name}
                    />
                  </div>
                </label>

                <label className="mt-2 block">
                  <span className="mb-3 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                    Add Team Members
                  </span>
                  <div className="rounded-lg border border-outline-variant/30 bg-surface-container-lowest p-3">
                    <AppCombobox
                      multiple
                      value={Array.isArray(state.form.team_member_user_ids) ? state.form.team_member_user_ids : []}
                      onChange={(value) =>
                        actions.patchForm('team_member_user_ids', Array.isArray(value) ? value : [])
                      }
                      fetchOptions={actions.fetchManagerOptions}
                      placeholder="Select team members"
                      searchPlaceholder="Search users..."
                      selectedLabelOverride="Team members selected"
                      triggerClassName="w-full border-none bg-transparent p-0 text-sm font-medium text-on-surface"
                    />
                    {selectedTeamMembers.length ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {selectedTeamMembers.map((member) => (
                          <span
                            key={member.id}
                            className="inline-flex items-center gap-2 rounded-full border border-outline-variant/40 bg-surface-container-lowest px-3 py-1.5"
                          >
                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary-fixed text-[10px] font-black text-primary">
                              {initialsFromName(member.name)}
                            </span>
                            <span className="text-xs font-semibold text-on-surface">{member.name}</span>
                            <button
                              type="button"
                              onClick={() =>
                                actions.patchForm(
                                  'team_member_user_ids',
                                  (Array.isArray(state.form.team_member_user_ids) ? state.form.team_member_user_ids : []).filter(
                                    (value) => String(value) !== String(member.id),
                                  ),
                                )
                              }
                              className="material-symbols-outlined text-sm text-on-surface-variant hover:text-error"
                              aria-label={`Remove ${member.name}`}
                            >
                              close
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </label>

                <div>
                  <span className="mb-3 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Client Contacts</span>
                  <div className="space-y-2">
                    {visibleContacts.length ? (
                      visibleContacts.map((row, index) => {
                        const displayPhone = Array.isArray(row.phoneNumbers)
                          ? row.phoneNumbers.filter(Boolean).join(', ')
                          : Array.isArray(row.phone_numbers)
                          ? row.phone_numbers.filter(Boolean).join(', ')
                          : row.phone || ''
                        return (
                            <div key={`${row.email || row.name}-${index}`} className="flex items-center gap-3 rounded-lg bg-white/50 p-3">
                             <div className="flex h-8 w-8 items-center justify-center rounded-full bg-tertiary-fixed text-xs font-bold">
                               {initialsFromName(row.name)}
                             </div>
                             <div className="min-w-0 flex-1">
                               <p className="truncate text-xs font-bold text-on-surface">{row.name}</p>
                               <p className="truncate text-[10px] text-on-surface-variant">{String(row.role || '').replaceAll('_', ' ')}</p>
                               {/* Gap 6: Click-to-Call phone numbers */}
                               {displayPhone ? (
                                 <span className="flex items-center gap-1 text-[10px] text-on-surface-variant">
                                   <span>Phone: {displayPhone}</span>
                                   {makeCall && (
                                     <button
                                       type="button"
                                       onClick={() => {
                                         const firstPhone = displayPhone.split(',')[0].trim()
                                         if (firstPhone) makeCall(firstPhone, { name: row.name, project_name: state.form.name })
                                       }}
                                       title={`Call ${displayPhone}`}
                                       className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-700 transition-colors"
                                     >
                                       <span className="material-symbols-outlined" style={{ fontSize: '12px' }}>call</span>
                                     </button>
                                   )}
                                 </span>
                               ) : null}
                               {row.email ? <p className="truncate text-[10px] text-on-surface-variant">Email: {row.email}</p> : null}
                             </div>
                             <button
                               type="button"
                               onClick={() => onEditStakeholder(row.sourceIndex)}
                               className="ml-auto rounded-full p-1 text-on-surface-variant hover:bg-surface-container-lowest"
                               title="Edit stakeholder"
                             >
                               <span className="material-symbols-outlined text-sm">edit</span>
                             </button>
                           </div>
                        )
                      })
                    ) : (
                      <div className="rounded-lg bg-white/50 p-3 text-xs text-on-surface-variant">No stakeholder added yet.</div>
                    )}

                    <button
                      type="button"
                      onClick={onOpenStakeholderModal}
                      className="flex w-full items-center justify-center gap-2 rounded-lg bg-surface-container-lowest py-2 text-[10px] font-bold uppercase tracking-widest text-primary transition-colors hover:bg-white"
                    >
                      <span className="material-symbols-outlined text-xs">person_add</span>
                      Add Stakeholder
                    </button>
                  </div>
                </div>

                <div className="mt-4 border-t border-outline-variant/30 pt-4">
                  <p className="mb-3 text-xs font-bold uppercase tracking-wider text-on-surface-variant">Quick Validation</p>
                  <ul className="space-y-2">
                    <li className={`flex items-center gap-2 text-[10px] font-medium ${hasMapping ? 'text-green-600' : 'text-on-surface-variant/40'}`}>
                      <span className="material-symbols-outlined text-xs">{hasMapping ? 'check_circle' : 'radio_button_unchecked'}</span>
                      Entity mapping verified
                    </li>
                    <li className="flex items-center gap-2 text-[10px] font-medium text-amber-600">
                      <span className="material-symbols-outlined text-xs">warning</span>
                      Project code uniqueness check pending
                    </li>
                    <li className={`flex items-center gap-2 text-[10px] font-medium ${hasPartialStakeholder ? 'text-amber-600' : 'text-on-surface-variant/40'}`}>
                      <span className="material-symbols-outlined text-xs">{hasPartialStakeholder ? 'warning' : 'radio_button_unchecked'}</span>
                      Financial audit clearance required
                    </li>
                  </ul>
                </div>
              </div>
            </article>

            <article className="rounded-2xl bg-surface-container-high p-8">
              <div className="mb-4 flex items-center gap-3">
                <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>
                  check_circle
                </span>
                <h3 className="text-xl font-bold text-on-surface">Completion</h3>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-surface-container-lowest p-4">
                <div>
                  <p className="text-sm font-bold text-on-surface">Mark Project Completed</p>
                  {/* <p className="text-xs text-on-surface-variant">Sets `is_completed` to 1 for this project.</p> */}
                </div>
                <AppToggle
                  checked={Boolean(state.form.is_completed)}
                  onChange={(next) => actions.patchForm('is_completed', next ? 1 : 0)}
                  ariaLabel="Toggle project completion"
                />
              </div>
            </article>
            
                    <div className="flex gap-3">
          <button
            type="button"
            onClick={actions.handleDiscard}
            className="rounded-md bg-secondary-container px-6 py-3 text-base font-bold text-on-secondary-container transition-all hover:bg-surface-container-highest"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={actions.handleSave}
            disabled={state.isSaving || state.isLoading}
            className="rounded-md bg-primary px-8 py-3 text-base font-bold text-on-primary shadow-lg transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {state.success ? 'Success! ✓' : state.isSaving ? 'Saving...' : state.isEditMode ? 'Save Changes' : 'Deploy Project'}
          </button>
        </div>

          </aside>
        </div>
      )}

      {isDisableTeamModalOpen ? (
        <div className="fixed inset-0 z-[69] flex items-center justify-center bg-black/35 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-2xl">
            <h4 className="text-xl font-bold text-on-surface">Disable Team + Channel Sync?</h4>
            <p className="mt-3 text-sm text-on-surface-variant">
              Turning this off will archive the project chat channels. Chat history is not preserved, and the channel becomes inactive.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsDisableTeamModalOpen(false)}
                className="rounded-md bg-secondary-container px-5 py-2 text-sm font-bold text-on-secondary-container"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  actions.patchForm('auto_create_team', false)
                  setIsDisableTeamModalOpen(false)
                }}
                className="rounded-md bg-error px-5 py-2 text-sm font-bold text-on-error"
              >
                Disable
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isStakeholderModalOpen ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/35 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-2xl">
            <div className="mb-5 flex items-center justify-between">
              <h4 className="text-xl font-bold text-on-surface">{editingStakeholderIndex === null ? 'Add Client Contact' : 'Edit Client Contact'}</h4>
              <button type="button" onClick={onCloseStakeholderModal} className="rounded-full p-1 text-on-surface-variant hover:bg-surface-container-low">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            {stakeholderDraftError ? (
              <div className="mb-4 rounded-lg border border-error/30 bg-error-container px-3 py-2 text-xs text-error">{stakeholderDraftError}</div>
            ) : null}

            <div className="grid grid-cols-1 gap-4">
              <label>
                <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                  Role <span className="text-error">*</span>
                </span>
                <AppDropdown
                  value={stakeholderDraft.role}
                  onChange={(value) => setStakeholderDraft((prev) => ({ ...prev, role: value }))}
                  triggerClassName="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface"
                  options={stakeholderRoles.map((role) => ({
                    value: role,
                    label: role.replaceAll('_', ' '),
                  }))}
                />
              </label>

              <label>
                <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                  Name <span className="text-error">*</span>
                </span>
                <input
                  type="text"
                  value={stakeholderDraft.name}
                  onChange={(event) => setStakeholderDraft((prev) => ({ ...prev, name: event.target.value }))}
                  className="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none"
                />
              </label>

              <div>
                <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                  Phone Number(s) <span className="text-error">*</span>
                </span>
                <div className="space-y-3">
                  {(stakeholderDraft.phoneNumbers || ['']).map((phone, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={phone}
                        onChange={(event) => {
                          const updated = [...(stakeholderDraft.phoneNumbers || [''])]
                          updated[idx] = event.target.value
                          setStakeholderDraft((prev) => ({ ...prev, phoneNumbers: updated }))
                        }}
                        placeholder={`Phone Number #${idx + 1}`}
                        className={`w-full flex-1 rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none ${
                          phone.trim() && !isValidStakeholderPhone(phone) ? 'ring-2 ring-error/35' : ''
                        }`}
                      />
                      {(stakeholderDraft.phoneNumbers || ['']).length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const updated = (stakeholderDraft.phoneNumbers || ['']).filter((_, i) => i !== idx)
                            setStakeholderDraft((prev) => ({ ...prev, phoneNumbers: updated }))
                          }}
                          className="rounded-lg p-2 text-error hover:bg-error/10 transition-colors"
                          title="Remove phone number"
                        >
                          <span className="material-symbols-outlined align-middle">delete</span>
                        </button>
                      )}
                    </div>
                  ))}
                  {(stakeholderDraft.phoneNumbers || ['']).length < 10 && (
                    <button
                      type="button"
                      onClick={() => {
                        setStakeholderDraft((prev) => ({
                          ...prev,
                          phoneNumbers: [...(prev.phoneNumbers || ['']), ''],
                        }))
                      }}
                      className="flex items-center gap-1 text-xs font-bold text-primary hover:underline mt-1"
                    >
                      <span className="material-symbols-outlined text-sm">add</span>
                      Add Phone Number
                    </button>
                  )}
                </div>
                {stakeholderPhoneError ? <p className="mt-1 text-[11px] font-semibold text-error">{stakeholderPhoneError}</p> : null}
              </div>

              <label>
                <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                  Email <span className="text-error">*</span>
                </span>
                <input
                  type="email"
                  value={stakeholderDraft.email}
                  onChange={(event) => setStakeholderDraft((prev) => ({ ...prev, email: event.target.value }))}
                  className={`w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none ${
                    stakeholderEmailError ? 'ring-2 ring-error/35' : ''
                  }`}
                />
                {stakeholderEmailError ? <p className="mt-1 text-[11px] font-semibold text-error">{stakeholderEmailError}</p> : null}
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={onCloseStakeholderModal}
                className="rounded-md bg-secondary-container px-5 py-2 text-sm font-bold text-on-secondary-container"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onSubmitStakeholder}
                disabled={Boolean(stakeholderPhoneError || stakeholderEmailError)}
                className="rounded-md bg-primary px-5 py-2 text-sm font-bold text-on-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {editingStakeholderIndex === null ? 'Save Contact' : 'Update Contact'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
